import React, { useState } from 'react';
import axios from 'axios';
import { Bell, Edit, Save, X } from 'lucide-react';
import Button from './Button';
import { useToast } from '../context/ToastContext';

interface ReminderSettingsSaved {
  playerDaysAhead: number;
  captainDaysAhead: number;
  playerEnabled: boolean;
  captainEnabled: boolean;
}

interface TeamReminderSettingsCardProps {
  teamId: number;
  playerDaysAhead: number;
  captainDaysAhead: number;
  /** Team-level on/off switches; default to enabled when omitted. */
  playerEnabled?: boolean;
  captainEnabled?: boolean;
  canManage: boolean;
  onSaved: (values: ReminderSettingsSaved) => void;
}

const DEFAULT_PLAYER_DAYS = 3;
const DEFAULT_CAPTAIN_DAYS = 5;
const MIN_DAYS = 1;
const MAX_DAYS = 14;

const PLAYER_HELP = "Only sent once the lineup is published, and only to players who haven't confirmed.";
const CAPTAIN_HELP = 'Only sent while the lineup is still unpublished.';

const clampDays = (value: number, fallback: number): number => {
  if (Number.isNaN(value)) return fallback;
  return Math.min(MAX_DAYS, Math.max(MIN_DAYS, value));
};

const dayUnit = (days: number) => (days === 1 ? 'day' : 'days');

/** A labelled on/off switch (reuses the app's NotificationSettings toggle style). */
const Toggle: React.FC<{ label: string; checked: boolean; onChange: (next: boolean) => void }> = ({
  label,
  checked,
  onChange,
}) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
        checked ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

/**
 * Configuration-tab card for per-team reminders. Each reminder type has a
 * team-level on/off switch plus a lead-time (days-ahead) setting. Owns its own
 * edit state and persists via a partial PUT to /teams/{id} (the backend applies
 * only the fields it receives), then reports the saved values back to the parent.
 */
const TeamReminderSettingsCard: React.FC<TeamReminderSettingsCardProps> = ({
  teamId,
  playerDaysAhead,
  captainDaysAhead,
  playerEnabled = true,
  captainEnabled = true,
  canManage,
  onSaved,
}) => {
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [playerDays, setPlayerDays] = useState(playerDaysAhead);
  const [captainDays, setCaptainDays] = useState(captainDaysAhead);
  const [playerOn, setPlayerOn] = useState(playerEnabled);
  const [captainOn, setCaptainOn] = useState(captainEnabled);

  const handleEdit = () => {
    setPlayerDays(playerDaysAhead);
    setCaptainDays(captainDaysAhead);
    setPlayerOn(playerEnabled);
    setCaptainOn(captainEnabled);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/teams/${teamId}`, {
        reminder_player_days_ahead: playerDays,
        reminder_captain_days_ahead: captainDays,
        reminder_player_enabled: playerOn,
        reminder_captain_enabled: captainOn,
      });
      toast.success('Reminder settings updated successfully');
      onSaved({
        playerDaysAhead: playerDays,
        captainDaysAhead: captainDays,
        playerEnabled: playerOn,
        captainEnabled: captainOn,
      });
      setIsEditing(false);
    } catch (error: any) {
      const message = error?.response?.data?.detail || 'Failed to update reminder settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reminders</h3>
        </div>
        {canManage && !isEditing && (
          <Button onClick={handleEdit} variant="outline" size="sm">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-6">
          {/* Player confirmation reminders */}
          <div className="space-y-3">
            <Toggle
              label="Send player confirmation reminders"
              checked={playerOn}
              onChange={setPlayerOn}
            />
            <div className={playerOn ? '' : 'opacity-50'}>
              <label
                htmlFor="reminder_player_days_ahead"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Remind unconfirmed players this many days before a match
              </label>
              <input
                id="reminder_player_days_ahead"
                type="number"
                min={MIN_DAYS}
                max={MAX_DAYS}
                disabled={!playerOn}
                value={playerDays}
                onChange={(e) => setPlayerDays(clampDays(parseInt(e.target.value, 10), DEFAULT_PLAYER_DAYS))}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{PLAYER_HELP}</p>
            </div>
          </div>

          {/* Captain publish reminders */}
          <div className="space-y-3">
            <Toggle
              label="Send captain publish reminders"
              checked={captainOn}
              onChange={setCaptainOn}
            />
            <div className={captainOn ? '' : 'opacity-50'}>
              <label
                htmlFor="reminder_captain_days_ahead"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Remind captains to publish this many days before a match
              </label>
              <input
                id="reminder_captain_days_ahead"
                type="number"
                min={MIN_DAYS}
                max={MAX_DAYS}
                disabled={!captainOn}
                value={captainDays}
                onChange={(e) => setCaptainDays(clampDays(parseInt(e.target.value, 10), DEFAULT_CAPTAIN_DAYS))}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{CAPTAIN_HELP}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving} size="sm">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={handleCancel} variant="outline" size="sm" disabled={saving}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <dl className="space-y-6">
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              Player confirmation reminders
            </dt>
            <dd>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{PLAYER_HELP}</p>
              {playerEnabled ? (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-semibold tabular-nums text-gray-900 dark:text-gray-100 leading-none">
                    {playerDaysAhead}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {dayUnit(playerDaysAhead)}
                  </span>
                </div>
              ) : (
                <span className="text-3xl font-semibold text-gray-400 dark:text-gray-500 leading-none">
                  Off
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              Captain publish reminders
            </dt>
            <dd>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{CAPTAIN_HELP}</p>
              {captainEnabled ? (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-semibold tabular-nums text-gray-900 dark:text-gray-100 leading-none">
                    {captainDaysAhead}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {dayUnit(captainDaysAhead)}
                  </span>
                </div>
              ) : (
                <span className="text-3xl font-semibold text-gray-400 dark:text-gray-500 leading-none">
                  Off
                </span>
              )}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
};

export default TeamReminderSettingsCard;
